import { Box, Flex, Text, IconButton, Spinner } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { Tooltip } from '../../../../components/Tooltip'
import WysiwygContent from '../../../../components/WysiwygContent'
import { formatDateTime, useAppToast } from '../../../../helpers'
import { BlogPostService } from '../../../../services/BlogPost/BlogPostService'
import { BlogPostDraft } from '../../../../services/BlogPost/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSelectDraft: (draft: BlogPostDraft) => void
}

export function DraftsModal({ isOpen, onClose, onSelectDraft }: IProps) {
  const service = new BlogPostService()
  const toast = useAppToast()
  const [drafts, setDrafts] = useState<BlogPostDraft[]>([])
  const [loading, setLoading] = useState(false)
  const [draftToDelete, setDraftToDelete] = useState<BlogPostDraft | null>(null)
  /** Rascunho sendo aberto: barra um segundo clique enquanto o rascunho completo não chega. */
  const [openingId, setOpeningId] = useState<number | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadDrafts()
    }
  }, [isOpen])

  const loadDrafts = async () => {
    setLoading(true)
    service
      .listDrafts()
      .then(setDrafts)
      .catch(() => {
        toast.error('Erro ao carregar rascunhos')
      })
      .finally(() => setLoading(false))
  }

  // Devolve a promessa para o ConfirmDialog cuidar do loading do botão.
  const handleDelete = async () => {
    if (!draftToDelete) return

    return service
      .deleteDraft(draftToDelete.id)
      .then(() => {
        toast.success('Rascunho excluído')
        setDraftToDelete(null)
        loadDrafts()
      })
      .catch(() => {
        toast.error('Erro ao excluir rascunho')
      })
  }

  const handleSelect = (draft: BlogPostDraft) => {
    if (openingId !== null) return
    setOpeningId(draft.id)

    // Fetch full draft with form_data
    service
      .getDraft(draft.id)
      .then((fullDraft) => {
        onSelectDraft(fullDraft)
        onClose()
      })
      .catch(() => {
        toast.error('Erro ao carregar rascunho')
      })
      .finally(() => setOpeningId(null))
  }

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Rascunhos">
      {loading ? (
        <LoadingBlock />
      ) : drafts.length === 0 ? (
        <Text textAlign="center" color="gray.500" py={8}>
          Nenhum rascunho salvo
        </Text>
      ) : (
        <Flex direction="column" gap={2}>
          {drafts.map((draft) => (
            <Flex
              key={draft.id}
              p={3}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              cursor={openingId !== null ? 'wait' : 'pointer'}
              _hover={{ bg: 'gray.50' }}
              onClick={() => handleSelect(draft)}
              justify="space-between"
              align="flex-start"
            >
              <Box flex={1} mr={3} overflow="hidden">
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                  {draft.title || 'Sem título'}
                </Text>
                <WysiwygContent
                  content={draft.body || 'Sem conteúdo'}
                  fontSize="xs"
                  color="gray.600"
                  noOfLines={3}
                  mt={1}
                  sx={{
                    '& *': { fontSize: 'xs', margin: 0, padding: 0 }
                  }}
                />
                <Text variant="caption" mt={1}>
                  {formatDateTime(draft.created_at)}
                </Text>
              </Box>
              {openingId === draft.id && <Spinner size="sm" color="magenta.500" mr={2} alignSelf="center" />}
              <Tooltip label="Excluir rascunho">
                <IconButton
                  aria-label="Excluir rascunho"
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDraftToDelete(draft)
                  }}
                >
                  <Icon name="lni lni-trash-can" />
                </IconButton>
              </Tooltip>
            </Flex>
          ))}
        </Flex>
      )}

      <ConfirmDialog
        isOpen={!!draftToDelete}
        onClose={() => setDraftToDelete(null)}
        onConfirm={handleDelete}
        action="delete"
        entity="rascunho"
        name={draftToDelete?.title || undefined}
      />
    </FormModal>
  )
}
